import React, { ReactNode, useContext, useEffect, useState } from "react";
import { LTabStrip, LFade, LDialog } from "@loffa/gpp-modules";
import { GeneralInfoForm } from "./ModalFormSteps/GeneralInfoForm";
import { SourceFileForm } from "./ModalFormSteps/SourceFileForm";
import { ValidationsForm } from "./ModalFormSteps/ValidationsForm";
import { OutputDestinations } from "./ModalFormSteps/OutputDestinations/OutputDestinations";
import { Notifications } from "./ModalFormSteps/Notifications/Notifications";
import { Confirmation } from "./ModalFormSteps/Confirmation/Confirmation";
import styles from "./AddEditAutoImportConfigurationsModal.module.css";
import { PgpDecryptionForm } from "./ModalFormSteps/PgpDecryptionForm";
import { useAutoImportFormContext } from "../Context/AutoImportFormContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk, faCheck } from "@fortawesome/sharp-solid-svg-icons";
import { pgpDecryptionValidations,validateGeneralInfo,validateSourceFile,validateValidations} from "./utils/formValidations";
import { AuthContext } from "../../../../../../../context/AuthContext";
import { AutoImportConfigurationData } from "../AutoImportConfigurations";

interface AddEditAutoImportConfigurationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  initialData?: AutoImportConfigurationData | null;
}

interface Tab {
  title: string | ReactNode;
  body: ReactNode;
}

function AddEditAutoImportConfigurationsModal({isOpen, onClose, onSave, initialData = null}: AddEditAutoImportConfigurationsModalProps) {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { formData, resetFormData, setFormData, setError, error } = useAutoImportFormContext();
  const authContext = useContext(AuthContext);
  const token: string = authContext.authState().accessToken.accessToken;
  
  useEffect(()=>{
    if(initialData){
      setFormData(initialData)
    }
  },[initialData])

  if (!isOpen) return null;

  const isGeneralComplete = validateGeneralInfo(formData);
  const isSourceFileComplete = validateSourceFile(formData);
  const isValidationsComplete = validateValidations(formData);
  const isPgpDecryptionComplete = pgpDecryptionValidations(formData);
  const outputDestinationsCount = formData.outputDestinations?.length;
  const notificationsCount = formData.notifications?.length;

  const handleSave = async () => {
    let reqUrl;
    if(initialData?.id){
      reqUrl = `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConfigurations/${initialData.id}`
    }else{
      reqUrl = `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConfigurations`
    }
    try {
      const response = await fetch(reqUrl, {
          method: !initialData ? "POST" : "PUT",
          headers: { Authorization: `Bearer ${token}`},
          body: JSON.stringify(formData),
        }
      );
      if (!response.ok) throw new Error("Failed to save auto import configurations.");
    } catch (error: any) {
      setError("Error saving data: " + error.message);
    } finally {
      onSave()
    }
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  //helper function that formats tab title if all required fields are
  const getTabTitle = (baseTitle: string, isComplete: boolean) => (
    <span className={styles.tabTitle}>
      {baseTitle}{" "}
      {isComplete ? (
        <FontAwesomeIcon
          icon={faCheck}
          color="green"
          fontSize={8}
          className={styles.tabTitleIcon}
        />
      ) : (
        <FontAwesomeIcon
          icon={faAsterisk}
          color="red"
          fontSize={6}
          className={styles.tabTitleIcon}
        />
      )}
    </span>
  );

  const tabs: Tab[] = [
    {
      title: getTabTitle("General", isGeneralComplete),
      body: <GeneralInfoForm />,
    },
    {
      title: getTabTitle("Source File", isSourceFileComplete),
      body: <SourceFileForm />,
    },
    {
      title: getTabTitle("Validations", isValidationsComplete),
      body: <ValidationsForm />,
    },
    {
      title: getTabTitle("Pgp Decryption", isPgpDecryptionComplete),
      body: <PgpDecryptionForm />,
    },
    {
      title: `Output Destinations (${outputDestinationsCount})`,
      body: <OutputDestinations />,
    },
    {
      title: `Notifications (${notificationsCount})`,
      body: <Notifications />,
    },
    {
      title: <b>Confirm & Save</b>,
      body: <Confirmation onSave={handleSave} />,
    },
  ];

  const modalBody = (
    <div className={styles.autoImportConfigModalContent}>
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
      <LTabStrip
        selected={selectedTab}
        onSelect={(e: { selected: number }) => setSelectedTab(e.selected)}
        orientation="vertical"
        tabs={tabs}
        tabPosition="top"
      />
    </div>
  );

  return (
    <LFade transitionEnterDuration={300}>
      <LDialog
        width="950"
        title={`${initialData ? "Edit" : "Add"} Auto Import Configuration`}
        subtitle={<div className={styles.modalSubtitle}>Required Fields</div>}
        body={modalBody}
        actionLayout="end"
        actionCloseButton={handleClose}
        actionOverlay={handleClose}
      />
    </LFade>
  );
}

export { AddEditAutoImportConfigurationsModal };
